import { Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { axleCalc, diagonalCalc, railCalc, parallelCalc, fmtSigned, fmtAbs, fmtTol } from "../../../lib/calc";
import { newDiagonal, uid } from "../../../lib/model";
import { ActionButton, SitBadge, CalcCell, Panel } from "../../common/Field";
import { Cell } from "./PointsTable";

const th = "px-2 py-2 font-mono text-[10px] font-medium tracking-wider text-slate-400 whitespace-nowrap text-left";

export function AxlesTable({ insp, update }) {
  const reference = insp.reference || {};
  const set = (i, k, v) => update("axles", (list) => list.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  return (
    <div className="overflow-x-auto border border-[#1E2F45] rounded-sm">
      <table className="w-full" data-testid="table-eixos">
        <thead className="bg-[#050B10]"><tr className="border-b border-[#1E2F45]">
          {["Eixo", "Posição X (mm)", "Esquerda", "Centro", "Direita", "Dif. E−D", "Deslocamento", "Situação", "Observação"].map((h) => <th key={h} className={th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {(insp.axles || []).map((a, i) => {
            const c = axleCalc(a, reference);
            return (
              <tr key={a.eixo} className="border-b border-[#1E2F45]/60" data-testid={`axle-row-${i}`}>
                <td className="px-2 py-1 font-mono text-sm font-bold text-[#FFD500] whitespace-nowrap">{a.eixo}</td>
                <td className="px-1 py-1"><Cell testId={`axle-${i}-posicao`} value={a.posicao_x} onChange={(v) => set(i, "posicao_x", v)} className="text-[#7FB6EE]" /></td>
                {["esquerda", "centro", "direita"].map((k) => <td key={k} className="px-1 py-1"><Cell testId={`axle-${i}-${k}`} value={a[k]} onChange={(v) => set(i, k, v)} /></td>)}
                <td className="px-2 py-1 whitespace-nowrap"><CalcCell testId={`axle-${i}-diff`} danger={c.tol !== null && c.diffLR !== null && Math.abs(c.diffLR) > c.tol}>{fmtSigned(c.diffLR)}</CalcCell></td>
                <td className="px-1 py-1"><Cell testId={`axle-${i}-deslocamento`} value={a.deslocamento} onChange={(v) => set(i, "deslocamento", v)} /></td>
                <td className="px-2 py-1">{c.hasData ? <SitBadge testId={`axle-${i}-situacao`} situacao={c.situacao} /> : <span className="text-slate-600 font-mono text-xs">—</span>}</td>
                <td className="px-1 py-1"><Cell testId={`axle-${i}-obs`} value={a.observacao} onChange={(v) => set(i, "observacao", v)} num={false} w="min-w-[160px]" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="px-3 py-2 text-[11px] text-slate-500">Tolerância de eixo: {fmtTol(reference.tolerancia_eixo ? Math.abs(Number(String(reference.tolerancia_eixo).replace(",", "."))) : null)}. A posição X posiciona o eixo no mapa gráfico.</p>
    </div>
  );
}

export function DiagonalsTable({ insp, update }) {
  const { rows, pairs, tol } = diagonalCalc(insp.diagonals, insp.reference || {});
  const set = (i, k, v) => update("diagonals", (list) => list.map((d, j) => (j === i ? { ...d, [k]: v } : d)));
  const add = () => update("diagonals", (list) => [...(list || []), newDiagonal(list)]);
  const remove = (i) => update("diagonals", (list) => list.filter((_, j) => j !== i));
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-xs text-slate-400 max-w-2xl">Cadastre as diagonais em pares (D1×D2, D3×D4...) para comparação cruzada. Use os números dos pontos (ex.: P1 → P4) para desenhá-las no mapa.</p>
        <ActionButton icon={Plus} variant="outline" onClick={add} testId="btn-adicionar-diagonal">Adicionar diagonal</ActionButton>
      </div>
      <div className="overflow-x-auto border border-[#1E2F45] rounded-sm">
        <table className="w-full" data-testid="table-diagonais">
          <thead className="bg-[#050B10]"><tr className="border-b border-[#1E2F45]">
            {["Nome", "Ponto inicial", "Ponto final", "Medida (mm)", "Referência (mm)", "Diferença", "Situação", ""].map((h) => <th key={h} className={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((d, i) => (
              <tr key={d.id} className="border-b border-[#1E2F45]/60" data-testid={`diagonal-row-${i}`}>
                <td className="px-1 py-1"><Cell testId={`diagonal-${i}-nome`} value={d.nome} onChange={(v) => set(i, "nome", v)} num={false} w="w-16" className="font-bold text-[#FFD500]" /></td>
                <td className="px-1 py-1"><Cell testId={`diagonal-${i}-inicial`} value={d.ponto_inicial} onChange={(v) => set(i, "ponto_inicial", v)} num={false} /></td>
                <td className="px-1 py-1"><Cell testId={`diagonal-${i}-final`} value={d.ponto_final} onChange={(v) => set(i, "ponto_final", v)} num={false} /></td>
                <td className="px-1 py-1"><Cell testId={`diagonal-${i}-medida`} value={d.medida} onChange={(v) => set(i, "medida", v)} /></td>
                <td className="px-1 py-1"><Cell testId={`diagonal-${i}-referencia`} value={d.referencia} onChange={(v) => set(i, "referencia", v)} className="text-[#7FB6EE]" /></td>
                <td className="px-2 py-1 whitespace-nowrap"><CalcCell testId={`diagonal-${i}-diff`}>{fmtSigned(d.diff)}</CalcCell></td>
                <td className="px-2 py-1">{d.diff !== null ? <SitBadge situacao={d.situacao} /> : <span className="text-slate-600 text-xs">—</span>}</td>
                <td className="px-1 py-1"><button type="button" data-testid={`diagonal-${i}-remove`} onClick={() => remove(i)} className="h-8 w-8 flex items-center justify-center text-slate-500 hover:text-white"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <div className="py-8 text-center text-sm text-slate-500">Nenhuma diagonal cadastrada.</div>}
      </div>
      {pairs.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {pairs.map((p, i) => (
            <div key={p.a.id} className="border border-[#1E2F45] rounded-sm p-3 bg-[#050B10]" data-testid={`diagonal-pair-${i}`}>
              <div className="caption-label">Comparação {p.a.nome} × {p.b.nome}</div>
              <div className={cn("font-mono text-2xl font-bold mt-1", p.situacao === "FORA DA TOLERÂNCIA" ? "text-red-500" : "text-[#FFD500]")}>{fmtSigned(p.cross)}</div>
              <div className="mt-2 flex items-center gap-2"><SitBadge situacao={p.situacao} /><span className="font-mono text-[10px] text-slate-500">TOL {fmtTol(tol)}</span></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RailSide({ side, label, insp, update }) {
  const list = insp.rails?.[side] || [];
  const c = railCalc(list, insp.reference?.tolerancia_longarina, insp.reference || {});
  const setList = (fn) => update("rails", (r) => ({ ...(r || {}), [side]: fn((r || {})[side] || []) }));
  return (
    <Panel title={label} testId={`rail-${side}`} actions={<ActionButton variant="outline" icon={Plus} onClick={() => setList((l) => [...l, { id: uid(), descricao: "", desvio: "" }])} testId={`btn-add-rail-${side}`}>Ponto</ActionButton>}>
      <div className="flex flex-col gap-2">
        {list.map((p, i) => (
          <div key={p.id} className="flex gap-2 items-center" data-testid={`rail-${side}-row-${i}`}>
            <Cell testId={`rail-${side}-${i}-descricao`} value={p.descricao} onChange={(v) => setList((l) => l.map((x, j) => (j === i ? { ...x, descricao: v } : x)))} num={false} w="flex-1" />
            <Cell testId={`rail-${side}-${i}-desvio`} value={p.desvio} onChange={(v) => setList((l) => l.map((x, j) => (j === i ? { ...x, desvio: v } : x)))} w="w-24" />
            <span className="font-mono text-[10px] text-slate-500">mm</span>
            <button type="button" onClick={() => setList((l) => l.filter((_, j) => j !== i))} className="h-8 w-8 flex items-center justify-center text-slate-500 hover:text-white"><Trash2 size={14} /></button>
          </div>
        ))}
        {!list.length && <p className="text-xs text-slate-500">Registre o desvio medido em cada ponto da longarina.</p>}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-[#1E2F45]">
        <div><div className="caption-label">Desvio máx.</div><CalcCell testId={`rail-${side}-max`} className="text-base" danger={c.situacao === "FORA DA TOLERÂNCIA"}>{fmtAbs(c.max)}</CalcCell></div>
        <div><div className="caption-label">Desvio médio</div><CalcCell testId={`rail-${side}-mean`} className="text-base">{fmtAbs(c.mean)}</CalcCell></div>
        <div><div className="caption-label">Situação</div>{c.n ? <SitBadge situacao={c.situacao} /> : <span className="text-slate-600">—</span>}</div>
      </div>
    </Panel>
  );
}

export function RailsPanel({ insp, update }) {
  const par = insp.rails?.paralelismo || {};
  const c = parallelCalc(par, insp.reference || {});
  const setPar = (k) => (v) => update("rails", (r) => ({ ...(r || {}), paralelismo: { ...((r || {}).paralelismo || {}), [k]: v } }));
  return (
    <div className="flex flex-col gap-4">
      <div className="grid lg:grid-cols-2 gap-4">
        <RailSide side="esquerdo" label="Longarina esquerda" insp={insp} update={update} />
        <RailSide side="direito" label="Longarina direita" insp={insp} update={update} />
      </div>
      <Panel title="Paralelismo das longarinas" testId="panel-paralelismo">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div><div className="caption-label mb-1.5">Medição (mm)</div><Cell testId="par-medicao" value={par.medicao} onChange={setPar("medicao")} w="w-full" /></div>
          <div><div className="caption-label mb-1.5">Referência (mm)</div><Cell testId="par-referencia" value={par.referencia} onChange={setPar("referencia")} w="w-full" className="text-[#7FB6EE]" /></div>
          <div><div className="caption-label mb-1.5">Tolerância (± mm)</div><Cell testId="par-tolerancia" value={par.tolerancia} onChange={setPar("tolerancia")} w="w-full" /></div>
          <div><div className="caption-label mb-1.5">Diferença</div><CalcCell testId="par-diff" className="text-base">{fmtSigned(c.diff)}</CalcCell></div>
          <div><div className="caption-label mb-1.5">Situação</div>{c.diff !== null ? <SitBadge testId="par-situacao" situacao={c.situacao} /> : <span className="text-slate-600">—</span>}</div>
        </div>
      </Panel>
    </div>
  );
}
